import { zodResolver } from '@hookform/resolvers/zod';
import { adminChangePasswordSchema, type AdminChangePasswordInput } from '@bifeai/shared';
import { Button, Callout, Dialog, Field, Input, useToast } from '@bifeai/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { api, applyFieldErrors, errorMessage } from '../lib/api';

/** Cambiar la contraseña del panel: pide la actual, la nueva dos veces y el código de dos pasos. */
export function ChangePasswordDialog({ open, onClose, forced = false }: { open: boolean; onClose: () => void; forced?: boolean }) {
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);
  const [repeat, setRepeat] = useState('');
  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AdminChangePasswordInput>({ resolver: zodResolver(adminChangePasswordSchema) });
  const newPassword = watch('newPassword');

  const close = () => {
    reset();
    setRepeat('');
    setFormError(null);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    if (values.newPassword !== repeat) return setFormError('La nueva contraseña no coincide en los dos campos.');
    setFormError(null);
    try {
      await api.post('/platform/auth/change-password', values);
      toast.success('Contraseña actualizada', 'Se cerraron tus otras sesiones.');
      close();
    } catch (err) {
      if (!applyFieldErrors(err, setError)) setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && !isSubmitting && !forced && close()}
      title={forced ? 'Elegí tu contraseña' : 'Cambiar contraseña'}
      description={
        forced
          ? 'Entraste con una contraseña temporal. En "Contraseña actual" poné la que te llegó por email.'
          : 'Al cambiarla se cierran tus sesiones en otros dispositivos.'
      }
    >
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        <Field label="Contraseña actual" error={errors.currentPassword?.message}>
          <Input type="password" autoComplete="current-password" autoFocus {...register('currentPassword')} />
        </Field>
        <Field label="Nueva contraseña" hint="Al menos 10 caracteres, con letras y números." error={errors.newPassword?.message}>
          <Input type="password" autoComplete="new-password" {...register('newPassword')} />
        </Field>
        <Field label="Repetí la nueva contraseña" error={repeat && newPassword && repeat !== newPassword ? 'No coincide' : undefined}>
          <Input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
        </Field>
        <Field label="Código de verificación" hint="El de 6 números de tu app de autenticación." error={errors.code?.message}>
          <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} {...register('code')} />
        </Field>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <div className="bf-form-actions">
          {!forced && (
            <Button onClick={close} disabled={isSubmitting}>
              Cancelar
            </Button>
          )}
          <Button type="submit" variant="primary" loading={isSubmitting}>
            Cambiar contraseña
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
